import React from 'react';
import {
  FileText,
  ExternalLink,
  School,
  MapPin,
  Phone,
  Mail,
  Award,
  Users,
  Building2,
} from 'lucide-react';

const Disclosure = () => {
  return (
    <section className="relative min-h-screen bg-slate-50 text-slate-800">
      {/* HEADER */}
      <div className="relative bg-gradient-to-r from-blue-700 via-blue-800 to-purple-800 text-white overflow-hidden">
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-8 py-14 sm:py-20">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-white/10 border border-white/20 rounded-full mb-5">
            <School className="w-3.5 h-3.5 text-amber-300" />
            <span className="text-[11px] font-bold uppercase tracking-wider text-amber-300">
              Vidya Bharati Public School • Bhalki
            </span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-black uppercase tracking-tight mb-3">
            Mandatory Public Disclosure
          </h1>
          <p className="text-slate-200 text-sm sm:text-base max-w-2xl leading-relaxed">
            Information &amp; documents of Vidya Bharati Public School provided below for public reference.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10 sm:py-14 space-y-12">
        {/* ============ A. GENERAL INFORMATION ============ */}
        <div>
          <SectionHeader letter="A" title="General Information" icon={<School className="w-5 h-5" />} />
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-100 border-b border-slate-200">
                  <Th w="w-16">SL No.</Th>
                  <Th>Information / Document</Th>
                  <Th w="w-60">Details</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <InfoRow sl="1" label="School Name" value="Vidya Bharati Public School" icon={<School className="w-4 h-4 text-blue-600" />} />
                <InfoRow sl="2" label="School Address" value="Bhalki, Karnataka" icon={<MapPin className="w-4 h-4 text-blue-600" />} />
                <InfoRow sl="3" label="School Contact Details" value="+91 9731666652" icon={<Phone className="w-4 h-4 text-blue-600" />} />
                <InfoRow sl="4" label="Affiliation No." value="—" />
                <InfoRow sl="5" label="School Code" value="29050316803" />
                <InfoRow sl="6" label="Committee Address with Pin Code" value="585328" />
                <InfoRow sl="7" label="Principal Name &amp; Qualification" value="SHESHIKUMAR MULGE" />
                <InfoRow sl="8" label="School Email Id" value="vbpbhalki@gmail.com" icon={<Mail className="w-4 h-4 text-blue-600" />} />
              </tbody>
            </table>
          </div>
        </div>

        {/* ============ B. DOCUMENTS & INFORMATION ============ */}
        <div>
          <SectionHeader letter="B" title="Documents & Information" icon={<FileText className="w-5 h-5" />} />
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-100 border-b border-slate-200">
                  <Th w="w-16">SL No.</Th>
                  <Th>Document / Certificate</Th>
                  <Th w="w-44">View / Download</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {documents.map((doc, i) => (
                  <DocumentRow key={i} sl={String(i + 1)} label={doc.label} href={doc.file} />
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ============ C. ACADEMIC AND RESULT ============ */}
        <div>
          <SectionHeader letter="C" title="Academic And Result" icon={<Award className="w-5 h-5" />} />
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-100 border-b border-slate-200">
                  <Th w="w-16">SL No.</Th>
                  <Th>Information</Th>
                  <Th w="w-44">View / Download</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {academicItems.map((item, i) => (
                  <DocumentRow key={i} sl={String(i + 1)} label={item.label} href={item.file} />
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ============ D. STAFF (TEACHING) ============ */}
        <div>
          <SectionHeader letter="D" title="Staff (Teaching)" icon={<Users className="w-5 h-5" />} />
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-100 border-b border-slate-200">
                  <Th w="w-16">SL No.</Th>
                  <Th>Information</Th>
                  <Th w="w-44">View</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <InfoRow sl="1" label="Principal" value="SHESHIKUMAR MULGE" />
                <InfoRow sl="2" label="Total No. Of Teachers" value="21" />
                <SubRow label="T G T" value="11" />
                <SubRow label="P R T" value="05" />
                <SubRow label="P G T" value="00" />
                <InfoRow sl="3" label="Teachers Section Ratio" value="01:30" />
                <InfoRow sl="4" label="Detail of Special Educator" value="ROHIT VAIRAGE" />
                <InfoRow sl="5" label="Detail of Counselor And Wellness Teacher" value="DRAUPATHY" />
              </tbody>
            </table>
          </div>
        </div>

        {/* ============ E. INFRASTRUCTURE & FACILITIES ============ */}
        <div>
          <SectionHeader letter="E" title="Infrastructure & Facilities" icon={<Building2 className="w-5 h-5" />} />
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-100 border-b border-slate-200">
                  <Th w="w-16">SL No.</Th>
                  <Th>Information</Th>
                  <Th w="w-44">View</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <InfoRow sl="1" label="Total Campus Area of The School (in Sq. Mtr)" value="4481.92 Sq. Mtr" />
                <InfoRow sl="2" label="No. And Size of Class Rooms (in Sq. Mtr)" value=" 20 class rooms, (59.83 Sq. Mtr)" />
                <InfoRow sl="3" label="No. And Size of Laboratories (in Sq. Mtr)" value=" 04 laboratories, (59.83 Sq. Mtr)" />
                <InfoRow sl="4" label="No. of Girls Toilets" value="16" />
                <InfoRow sl="5" label="No. of Boys Toilets" value="16" />
                <InfoRow sl="6" label="Link of YouTube Video of the Inspection of School covering Infrastructure" value="—" />
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-lg p-4 text-sm text-amber-900">
          <strong className="font-bold">Note:</strong> The above information and documents are provided for public reference. Click any <em>View / Download</em> link to open the respective PDF.
        </div>
      </div>

    </section>
  );
};

/* ============ HELPERS ============ */

const Th = ({ children, w = '' }) => (
  <th className={`text-left px-4 py-3 font-bold text-slate-600 uppercase text-[11px] tracking-wider ${w}`}>
    {children}
  </th>
);

const SectionHeader = ({ letter, title, icon }) => (
  <div className="flex items-center gap-3 mb-4">
    <div className="flex items-center justify-center w-9 h-9 rounded-md bg-gradient-to-r from-blue-600 to-purple-600 text-white font-black text-sm">
      {letter}
    </div>
    <div className="flex items-center gap-2">
      <span className="text-blue-700">{icon}</span>
      <h2 className="text-lg sm:text-xl font-black uppercase tracking-tight text-blue-800">{title}</h2>
    </div>
  </div>
);

const InfoRow = ({ sl, label, value, icon }) => (
  <tr className="hover:bg-slate-50/70 transition-colors">
    <td className="px-4 py-3 text-slate-500 font-semibold">{sl}</td>
    <td className="px-4 py-3 text-slate-700 font-medium">
      <div className="flex items-center gap-2">
        {icon}
        <span>{label}</span>
      </div>
    </td>
    <td className="px-4 py-3 text-slate-600 font-semibold">{value || '—'}</td>
  </tr>
);

const SubRow = ({ label, value = '—' }) => (
  <tr className="bg-slate-50/60">
    <td className="px-4 py-2"></td>
    <td className="px-4 py-2 text-slate-500 font-medium pl-10 text-xs uppercase tracking-wider">{label}</td>
    <td className="px-4 py-2 text-slate-600 font-semibold">{value}</td>
  </tr>
);

const DocumentRow = ({ sl, label, href }) => (
  <tr className="hover:bg-slate-50/70 transition-colors">
    <td className="px-4 py-3 text-slate-500 font-semibold">{sl}</td>
    <td className="px-4 py-3 text-slate-700 font-medium">{label}</td>
    <td className="px-4 py-3">
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-blue-700 hover:text-purple-700 font-bold text-xs uppercase tracking-wider transition-colors group"
        >
          <FileText className="w-3.5 h-3.5" />
          <span>View / Download</span>
          <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
        </a>
      ) : (
        <span className="inline-flex items-center gap-1.5 text-slate-400 font-bold text-xs uppercase tracking-wider">
          <FileText className="w-3.5 h-3.5" />
          <span>View / Download</span>
        </span>
      )}
    </td>
  </tr>
);

/* ============================================================
   DATA — matched to your PDF files in public/documents/
============================================================ */

// Section B — Documents & Information
const documents = [
  { label: 'Affiliation Certificate',                     file: '' },
  { label: 'Recognition Certificate',                     file: '/documents/Recognition.pdf' },
  { label: 'Building Safety Certificate',                 file: '/documents/building safety.pdf' },
  { label: 'Fire Safety Certificate',                     file: '/documents/fire safety.pdf' },
  { label: 'Trust / Society',                             file: '/documents/society bylaws (adarsh education society).pdf' },
  { label: 'No Objection Certificate (NOC)',              file: '' },
  { label: 'Self Certification',                          file: '/documents/self certification.pdf' },
  { label: 'Water, Health And Sanitation Certificate',    file: '/documents/Water, Health and Sanitation Certificate.pdf' },
  { label: 'Land Certificate',                            file: '/documents/Land Certificate.pdf' },
  { label: 'Water Test Report',                           file: '/documents/Water Test Report.pdf' },
  { label: 'Build Up Carpet Area Certificate',            file: '/documents/Built-up Area.pdf' },
];

// Section C — Academic And Result

const academicItems = [
  { label: 'Academic Calendar',                           file: '' },
  { label: 'School Fee Structure',                        file: '/documents/School Fee Structure.pdf' },
  { label: 'S M C',                                       file: '/documents/school management committee(SMC).pdf' },
  { label: 'P T A (Members)',                             file: '/documents/parent teacher association(PTA).pdf' },
  { label: 'Result Of Board Examination',                 file: '' },
  { label: 'Mandatory Public Disclosure',                 file: '' },
];

export default Disclosure;
